/**
 * Automatic Discounts Module (Optimized) - AE Adapted
 *
 * Consulta la Storefront API para detectar descuentos automáticos
 * que aplican a los productos visibles en la página.
 *
 * Flujo:
 * 1. Recopila los variant IDs de todas las ae-product-card del DOM
 * 2. Crea un "shadow cart" (carrito invisible) via Storefront API
 * 3. Lee las discount allocations del cart response
 * 4. Actualiza badges y precios en el DOM
 *
 * Optimizaciones implementadas:
 * - Batch de hover prefetch (reduce llamadas API ~90%)
 * - Cache check en PDP antes de fetch
 * - Batches paralelos para >250 variants
 * - MutationObserver scoped a contenedor específico
 * - Timeout con AbortController en fetch
 * - Cache de formatMoney
 * - TTL: 30 minutos
 *
 * AE Adaptations:
 * - Card selector: ae-product-card instead of product-card
 * - Badge selector: ae-card__badge instead of product-badges__badge
 * - PLP price container: ae-card__price instead of [ref="priceContainer"]
 * - AE uses single card type (no simple/standard distinction)
 */

(function () {
  // ─── Constants ───────────────────────────────────────────────

  var CACHE_KEY = "auto-discounts-v2";
  var CACHE_TTL = 30 * 60 * 1000; // 30 minutos
  var API_VERSION = "2025-01";
  var BATCH_SIZE = 250; // Límite de líneas por cart en Storefront API
  var FETCH_TIMEOUT = 5000; // 5 segundos timeout
  var HOVER_BATCH_DELAY = 400; // ms para acumular hovers
  var MUTATION_DEBOUNCE = 300; // ms debounce para MutationObserver
  var MAX_RETRY_COUNT = 3; // Máximo de retries para fetchBatch

  // ─── State ────────────────────────────────────────────────────

  var queriedVariantIds = {}; // IDs ya consultados en esta sesión
  var hoverPrefetchedVariantIds = {}; // IDs ya encolados por hover prefetch
  var debounceTimer = null;
  var pendingMutationIds = []; // IDs acumulados entre debounces del observer
  var hoverPrefetchQueue = [];
  var hoverPrefetchTimer = null;
  // Cache para formatMoney. En la práctica son pocos precios únicos por sesión (~20-50),
  // pero ponemos un tope defensivo para no acumular memoria en caso patológico (ej. PLPs
  // con miles de productos cargados via infinite scroll).
  var formatMoneyCache = {};
  var FORMAT_MONEY_CACHE_MAX = 500;

  // ─── Helpers ─────────────────────────────────────────────────

  /**
   * Normaliza un título de descuento para comparación flexible.
   * Hoisted al  scope del IIFE para no recrear la función en cada llamada de checkMatchingDiscountFlag.
   *
   * Si el título contiene un tag de requisitos ([min:2], [*], etc.),
   * se elimina para que la comparación con data-discount-title funcione correctamente.
   * Ejemplo: "Verano [min:2]" → "verano"
   */
  function normalizeDiscountTitle(value) {
    return String(value || "")
      .replace(/\s*\[(?:min:\d+|\*)\]\s*/gi, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\s*%\s*/g, "%")
      .trim();
  }

  /**
   * Verifica si el título del descuento contiene un tag de requisitos.
   * Tags válidos:
   *   [min:2]  → requiere mínimo 2 items
   *   [*]      → tiene condiciones (cualquiera)
   * Presencia de cualquier tag → el descuento es condicional y NO se debe mostrar.
   */
  function discountHasRequirements(title) {
    return /\[(?:min:\d+|\*)\]/i.test(title || "");
  }

  /**
   * Convierte un variant ID numérico al formato GID de Shopify
   * Ejemplo: "12345" → "gid://shopify/ProductVariant/12345"
   */
  function toGID(variantId) {
    return "gid://shopify/ProductVariant/" + variantId;
  }

  /**
   * Extrae el ID numérico de un GID de Shopify
   * Ejemplo: "gid://shopify/ProductVariant/12345" → "12345"
   */
  function fromGID(gid) {
    return gid.split("/").pop();
  }

  /**
   * Formatea un precio usando el money_format de la tienda
   * Con cache para evitar recalcular
   */
  function formatMoney(amountInUnits, moneyFormat) {
    // Cache lookup
    var cacheKey = amountInUnits + "|" + moneyFormat;
    if (formatMoneyCache[cacheKey]) {
      return formatMoneyCache[cacheKey];
    }

    var amount = amountInUnits;
    var formatted = amount.toFixed(2);
    var parts = formatted.split(".");
    var dollars = parts[0];
    var cents = parts[1];

    // Formato con punto como separador de miles y coma decimal (EU)
    var withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + cents;

    // Formato con coma como separador de miles y punto decimal (US)
    var withDot = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + cents;

    // Sin decimales
    var rounded = Math.round(amount).toString();
    var roundedWithComma = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    var roundedWithDot = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var roundedWithApostrophe = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    // Suiza: apóstrofe como separador de miles
    var withApostrophe =
      dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + "." + cents;

    /** @type {Object<string, string>} */
    var placeholderMap = {
      amount: withDot,
      amount_with_comma_separator: withComma,
      amount_with_apostrophe_separator: withApostrophe,
      amount_no_decimals: roundedWithDot,
      amount_no_decimals_with_comma_separator: roundedWithComma,
      amount_no_decimals_with_apostrophe_separator: roundedWithApostrophe,
    };

    var result = moneyFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      /**
       * @param {string} match
       * @param {string} key
       * @returns {string}
       */
      function (match, key) {
        var value = placeholderMap[key];
        return typeof value === "string" ? value : match;
      },
    );

    // Tope defensivo: si el cache crece más allá del límite (caso patológico), lo reseteamos.
    // No usamos LRU porque el caso normal son <50 entradas y el reset es prácticamente gratis.
    if (Object.keys(formatMoneyCache).length >= FORMAT_MONEY_CACHE_MAX) {
      formatMoneyCache = {};
    }
    formatMoneyCache[cacheKey] = result;
    return result;
  }

  // ─── Cache ───────────────────────────────────────────────────

  /**
   * Lee descuentos del cache (sessionStorage)
   * Retorna null si no hay cache o si expiró
   */
  function getCachedDiscounts() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;

      var data = JSON.parse(raw);
      var isExpired = Date.now() - data.timestamp > CACHE_TTL;

      if (isExpired) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }

      return data.discounts;
    } catch (e) {
      return null;
    }
  }

  /**
   * Guarda descuentos en cache con timestamp
   */
  function setCachedDiscounts(discounts) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          discounts: discounts,
        }),
      );
    } catch (e) {
      // sessionStorage puede estar lleno o no disponible
    }
  }

  // ─── DOM ─────────────────────────────────────────────────────

  /**
   * Recopila todos los variant IDs únicos de las ae-product-card en la página
   * Retorna un array de strings (IDs numéricos)
   */
  function collectVariantIds() {
    var cards = document.querySelectorAll("ae-product-card[data-variant-id]");
    var seen = {};
    var ids = [];

    for (var i = 0; i < cards.length; i++) {
      var id = cards[i].getAttribute("data-variant-id");
      if (id && !seen[id]) {
        seen[id] = true;
        ids.push(id);
      }
    }

    return ids;
  }

  // ─── DOM Helpers (Shared logic PLP/PDP) ─────────────────────────

  /**
   * Limpia clases de ejecuciones anteriores en priceContainer
   * Evita contaminación del DOM entre updates
   */
  function resetPriceContainerClasses(priceContainer) {
    var contaminatedCompareEls = priceContainer.querySelectorAll(
      ".price--compare-hidden",
    );
    for (var ce = 0; ce < contaminatedCompareEls.length; ce++) {
      contaminatedCompareEls[ce].classList.remove("price--compare-hidden");
    }
    var contaminatedAutoEls = priceContainer.querySelectorAll(
      ".price--compare-auto",
    );
    for (var ae = 0; ae < contaminatedAutoEls.length; ae++) {
      contaminatedAutoEls[ae].classList.remove("price--compare-auto");
    }
  }

  /**
   * Busca flags de descuento que matchean con el título del discount
   * @param {Element} container - Elemento donde buscar flags
   * @param {string} discountTitle - Título del descuento
   * @param {boolean} [revealMatchingFlag=true] - Si debe revelar el flag al encontrar match
   * @returns {boolean} - Si encontró un flag que matchea
   */
  function checkMatchingDiscountFlag(
    container,
    discountTitle,
    revealMatchingFlag,
  ) {
    if (!discountTitle) return false;
    if (revealMatchingFlag !== false) revealMatchingFlag = true;

    var normalizedDiscountTitle = normalizeDiscountTitle(discountTitle);
    var promoFlags = container.querySelectorAll("[data-discount-title]");
    var hasMatching = false;
    for (var pf = 0; pf < promoFlags.length; pf++) {
      var promoFlag = promoFlags[pf];
      var flagDiscountTitle = promoFlag.getAttribute("data-discount-title");
      if (
        flagDiscountTitle &&
        normalizeDiscountTitle(flagDiscountTitle) === normalizedDiscountTitle
      ) {
        if (revealMatchingFlag) promoFlag.removeAttribute("hidden");
        hasMatching = true;
      }
    }
    return hasMatching;
  }

  /**
   * Actualiza un badge de descuento
   * @param {Element} badgeEl - Elemento badge
   * @param {number} percent - Porcentaje de descuento
   * @param {boolean} hasMatchingFlag - Si hay flag personalizado que matchea
   */
  function updateBadgeElement(badgeEl, percent, hasMatchingFlag) {
    if (!badgeEl) return;

    // Automatic discounts only render when a matching metafield flag exists.
    // The matching flag is revealed by checkMatchingDiscountFlag; the generic
    // percentage badge stays hidden to avoid showing unconfigured promotions.
    badgeEl.setAttribute("hidden", "");
  }

  /**
   * Oculta un badge (helper para casos donde siempre se oculta)
   */
  function hideBadge(badgeEl) {
    if (badgeEl) badgeEl.setAttribute("hidden", "");
  }

  /**
   * Emite un evento para que custom-price recalcule usando el precio efectivo.
   * @param {Element} card
   * @param {number} effectivePriceInUnits
   */
  function emitCustomPriceSync(card, effectivePriceInUnits) {
    if (
      !card ||
      typeof effectivePriceInUnits !== "number" ||
      isNaN(effectivePriceInUnits)
    )
      return;

    document.dispatchEvent(
      new CustomEvent("custom-price:sync", {
        detail: {
          card: card,
          effectivePrice: Math.round(effectivePriceInUnits * 100),
        },
      }),
    );
  }

  /**
   * Actualiza los atributos de variante/precio de una card cuando cambia la selección.
   * @param {Element} card
   * @param {Object} resource
   */
  function syncCardVariantAttributes(card, resource) {
    if (!card || !resource) return;

    if (resource.id != null) {
      card.setAttribute("data-variant-id", String(resource.id));
    }

    if (resource.price != null) {
      card.setAttribute("data-product-price", String(resource.price));
    }

    card.setAttribute(
      "data-compare-at-price",
      String(resource.compare_at_price || 0),
    );
  }

  /**
   * Sincroniza el badge nativo de sale según el compare_at_price actual.
   * @param {Element} card
   */
  function syncManualSaleBadge(card) {
    if (!card) return;

    // AE uses single card type - always use ae-card__badge selector
    var staticBadge = card.querySelector(
      ".ae-card__badge[data-discount-badge]",
    );

    if (!staticBadge) return;

    var compareAtPriceValue = parseInt(
      card.getAttribute("data-compare-at-price") || "0",
    );
    var productPriceValue = parseInt(
      card.getAttribute("data-product-price") || "0",
    );

    if (compareAtPriceValue > productPriceValue) {
      var manualPercent = Math.round(
        ((compareAtPriceValue - productPriceValue) / compareAtPriceValue) * 100,
      );
      staticBadge.textContent = manualPercent + "% Off";
      staticBadge.removeAttribute("hidden");
      return;
    }

    staticBadge.setAttribute("hidden", "");
  }

  /**
   * Limpia el estado visual de descuentos automáticos para una card específica.
   * @param {Element} card
   */
  function resetCardDiscountState(card) {
    if (!card) return;

    // AE uses single card type - always use ae-card__price selector
    var priceContainer = card.querySelector(".ae-card__price");

    if (priceContainer) {
      resetPriceContainerClasses(priceContainer);

      var autoDiscountPriceEl = priceContainer.querySelector(
        "[data-auto-discount-price]",
      );
      if (autoDiscountPriceEl) {
        autoDiscountPriceEl.textContent = "";
        autoDiscountPriceEl.setAttribute("hidden", "");
      }
    }

    hideBadge(card.querySelector(".ae-card__badge[data-auto-discount-badge]"));

    var promoFlags = card.querySelectorAll("[data-discount-title]");
    for (var i = 0; i < promoFlags.length; i++) {
      promoFlags[i].setAttribute("hidden", "");
    }

    syncManualSaleBadge(card);
  }

  /**
   * Muestra el precio con descuento y tacha el precio original
   * @param {Element} priceContainer - Contenedor de precios
   * @param {number} finalPrice - Precio final con descuento
   * @param {string} moneyFormat - Format string
   * @param {Element} [currentPriceEl] - Elemento del precio actual (opcional, se busca si no se pasa)
   */
  function showDiscountedPrice(
    priceContainer,
    finalPrice,
    moneyFormat,
    currentPriceEl,
  ) {
    var autoDiscountPriceEl = priceContainer.querySelector(
      "[data-auto-discount-price]",
    );
    if (!currentPriceEl) {
      currentPriceEl = priceContainer.querySelector(
        ".ae-card__price-current, .custom-product-price__current",
      );
    }

    if (!currentPriceEl || !autoDiscountPriceEl) return;

    if (currentPriceEl.classList.contains("custom-product-price__current")) {
      currentPriceEl.classList.add("price--compare-auto");
    } else {
      currentPriceEl.classList.add("ae-card__price-compare");
    }

    autoDiscountPriceEl.textContent = formatMoney(finalPrice, moneyFormat);
    autoDiscountPriceEl.removeAttribute("hidden");
  }

  /**
   * Aplica los descuentos al DOM:
   * - Muestra el badge con el porcentaje (-XX%)
   * - Tacha el precio original y muestra el precio con descuento
   * @param {Object} discounts - Objeto de descuentos por variant ID
   * @param {string} moneyFormat - Formato de moneda
   * @param {NodeList|Array} [targetCards] - Cards específicos a procesar (opcional)
   * @param {boolean} [skipSyncEvent] - Si true, omite emitir custom-price:sync (para uso interno)
   */
  function applyDiscountsToDOM(
    discounts,
    moneyFormat,
    targetCards,
    skipSyncEvent,
  ) {
    // Si targetCards existe, usarlo; si no, full scan (backward compatible)
    var cards =
      targetCards ||
      document.querySelectorAll("ae-product-card[data-variant-id]");

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var variantId = card.getAttribute("data-variant-id");
      var discount = discounts[variantId];

      if (!discount) continue;

      // AE uses single card type - no simple/standard distinction
      // Price container is always ae-card__price
      var priceContainer = card.querySelector(".ae-card__price");

      if (!priceContainer) continue;

      // RESET: Limpiar clases de ejecuciones anteriores
      resetPriceContainerClasses(priceContainer);

      // Ocultar auto-discount price por defecto
      var autoDiscountPriceEl = priceContainer.querySelector(
        "[data-auto-discount-price]",
      );
      if (autoDiscountPriceEl) {
        autoDiscountPriceEl.setAttribute("hidden", "");
      }

      // Check if there's a matching discount flag
      var hasMatchingFlag = checkMatchingDiscountFlag(
        card,
        discount.title,
        false,
      );
      if (!hasMatchingFlag) continue;

      // Usar data attribute del card, NO clases del DOM (que el JS puede haber modificado)
      var compareAtPriceValue = parseInt(
        card.getAttribute("data-compare-at-price") || "0",
      );
      var productPriceValue = parseInt(
        card.getAttribute("data-product-price") || "0",
      );
      // hasManualDiscount: el producto tiene compare_at_price seteado en Shopify
      var hasManualDiscount =
        compareAtPriceValue > 0 && compareAtPriceValue > productPriceValue;

      if (hasManualDiscount) {
        // El producto tiene compare_at_price Y descuento automático.
        // Comparamos cuál es mayor y mostramos solo ese.
        var manualPercent =
          compareAtPriceValue > 0
            ? Math.round(
                ((compareAtPriceValue - productPriceValue) /
                  compareAtPriceValue) *
                  100,
              )
            : 0;

        if (discount.percent <= manualPercent) {
          // El manual es mejor: mostrar compare_at_price tachado + precio de oferta
          // (El CSS ya lo muestra con el fix que hicimos)
          continue;
        }

        checkMatchingDiscountFlag(card, discount.title);

        // El automático es mejor: usar sistema auto-discount
        // AE uses ae-card__badge for all badge types
        var staticBadge = card.querySelector(
          ".ae-card__badge[data-discount-badge]",
        );

        updateBadgeElement(staticBadge, discount.percent, hasMatchingFlag);

        // Hide the intermediate manual sale price. PLP should show the
        // original compare-at reference plus the best final price only.
        var salePriceEl = priceContainer.querySelector(
          ".ae-card__price-current:not([data-auto-discount-price])",
        );

        if (salePriceEl) {
          salePriceEl.classList.add("price--compare-hidden");
        }

        // Mostrar precio con auto-discount
        var discountedPriceEl = priceContainer.querySelector(
          "[data-auto-discount-price]",
        );
        if (discountedPriceEl) {
          discountedPriceEl.textContent = formatMoney(
            discount.finalPrice,
            moneyFormat,
          );
          discountedPriceEl.removeAttribute("hidden");
        }

        if (!skipSyncEvent) {
          emitCustomPriceSync(card, discount.finalPrice);
        }
      } else {
        // Solo descuento automático: tachar el precio original y mostrar el precio final.
        // AE uses ae-card__badge for auto-discount badges
        var badge = card.querySelector(
          ".ae-card__badge[data-auto-discount-badge]",
        );

        updateBadgeElement(badge, discount.percent, hasMatchingFlag);

        checkMatchingDiscountFlag(card, discount.title);

        // Tachar precio original y mostrar precio con descuento
        showDiscountedPrice(priceContainer, discount.finalPrice, moneyFormat);

        if (!skipSyncEvent) {
          emitCustomPriceSync(card, discount.finalPrice);
        }
      }
    }
  }

  /**
   * Reaplica descuentos para una card después de un cambio de variante.
   * @param {Element} card
   * @param {string} variantId
   * @param {Object} config
   */
  function refreshDiscountsForCard(card, variantId, config) {
    if (!card || !variantId || !config || !config.token) return;

    resetCardDiscountState(card);

    var cached = getCachedDiscounts() || {};
    if (cached[variantId]) {
      applyDiscountsToDOM(cached, config.moneyFormat, [card]);
    }

    if (queriedVariantIds[variantId]) return;

    queriedVariantIds[variantId] = true;

    fetchDiscounts([variantId], config.shopDomain, config.token).then(
      function (discounts) {
        var latestCached = getCachedDiscounts() || {};
        var merged = Object.assign({}, latestCached);

        if (discounts[variantId]) {
          merged[variantId] = discounts[variantId];
        } else {
          delete merged[variantId];
        }

        setCachedDiscounts(merged);
        resetCardDiscountState(card);

        if (merged[variantId]) {
          applyDiscountsToDOM(merged, config.moneyFormat, [card]);
        }
      },
    );
  }

  /**
   * Maneja cambios de variante emitidos por el tema en cards estándar.
   * @param {Event} event
   */
  function handleThemeVariantUpdate(event) {
    var config = window.__storefrontApi;
    var detail = event.detail;
    var target = event.target;
    // AE uses ae-product-card custom element
    var card =
      target && target.closest ? target.closest("ae-product-card") : null;

    if (!config || !config.token || !detail || !detail.resource) return;

    if (card && detail.resource.id) {
      syncCardVariantAttributes(card, detail.resource);

      requestAnimationFrame(function () {
        refreshDiscountsForCard(card, String(detail.resource.id), config);
      });
      return;
    }

    var productPrice =
      target && target.closest ? target.closest("product-price") : null;
    if (!productPrice) return;

    if (detail.resource.id != null) {
      productPrice.setAttribute("data-variant-id", String(detail.resource.id));
    }
    if (detail.resource.price != null) {
      productPrice.setAttribute(
        "data-product-price",
        String(detail.resource.price),
      );
    }
    productPrice.setAttribute(
      "data-compare-at-price",
      String(detail.resource.compare_at_price || 0),
    );

    var variantId = String(detail.resource.id);
    var galleryContainer = document.querySelector(
      ".gallery-main-container[data-variant-id]",
    );
    if (galleryContainer) {
      galleryContainer.setAttribute("data-variant-id", variantId);
    }

    var cached = getCachedDiscounts() || {};
    if (cached[variantId]) {
      applyDiscountToPDP(cached[variantId], config.moneyFormat);
      queriedVariantIds[variantId] = true;
      return;
    }

    queriedVariantIds[variantId] = true;
    fetchDiscounts([variantId], config.shopDomain, config.token).then(
      function (discounts) {
        var merged = Object.assign({}, cached, discounts);
        setCachedDiscounts(merged);
        applyDiscountToPDP(discounts[variantId] || null, config.moneyFormat);
      },
    );
  }

  // ─── Storefront API ──────────────────────────────────────────

  /**
   * Mutation GraphQL para crear un shadow cart
   */
  var CART_CREATE_MUTATION = [
    "mutation cartCreate($lines: [CartLineInput!]!) {",
    "  cartCreate(input: { lines: $lines }) {",
    "    cart {",
    "      lines(first: 250) {",
    "        edges {",
    "          node {",
    "            merchandise {",
    "              ... on ProductVariant {",
    "                id",
    "              }",
    "            }",
    "            cost {",
    "              amountPerQuantity {",
    "                amount",
    "                currencyCode",
    "              }",
    "            }",
    "            discountAllocations {",
    "              discountedAmount {",
    "                amount",
    "                currencyCode",
    "              }",
    "              ... on CartAutomaticDiscountAllocation {",
    "                title",
    "              }",
    "            }",
    "          }",
    "        }",
    "      }",
    "    }",
    "    userErrors {",
    "      field",
    "      message",
    "    }",
    "  }",
    "}",
  ].join("\n");

  /**
   * Envía la mutation a la Storefront API con timeout
   * Retorna la data del response o null si falla
   */
  function callStorefrontAPI(shopDomain, token, query, variables) {
    var url = "https://" + shopDomain + "/api/" + API_VERSION + "/graphql.json";

    // AbortController para timeout
    var controller = new AbortController();
    var timeoutId = setTimeout(function () {
      controller.abort();
    }, FETCH_TIMEOUT);

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query: query, variables: variables }),
      signal: controller.signal,
    })
      .then(function (response) {
        clearTimeout(timeoutId);
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function (error) {
        clearTimeout(timeoutId);
        if (error.name !== "AbortError") {
          console.warn("[AutoDiscounts] Error en la llamada API:", error);
        }
        return null;
      });
  }

  /**
   * Parsea el response del cart y extrae los descuentos por variant
   */
  function parseDiscounts(responseData) {
    var discounts = {};

    if (!responseData || !responseData.data) return discounts;

    var cart = responseData.data.cartCreate;
    if (!cart || !cart.cart || !cart.cart.lines) return discounts;

    var edges = cart.cart.lines.edges;

    for (var i = 0; i < edges.length; i++) {
      var line = edges[i].node;
      var variantId = fromGID(line.merchandise.id);
      var allocations = line.discountAllocations;

      if (!allocations || allocations.length === 0) continue;

      var totalDiscount = 0;
      var discountTitle = "";

      for (var j = 0; j < allocations.length; j++) {
        var amount = parseFloat(allocations[j].discountedAmount.amount);
        if (amount <= 0) continue;
        totalDiscount += amount;
        if (!discountTitle && allocations[j].title) {
          discountTitle = allocations[j].title;
        }
      }

      if (totalDiscount <= 0) continue;

      // Fail-safe: si el título lleva tag de requisitos ([min:2], [*], etc.),
      // este descuento es condicional y no aplica a una compra unitaria → no lo registramos.
      if (discountHasRequirements(discountTitle)) continue;

      var originalPrice = parseFloat(line.cost.amountPerQuantity.amount);
      var finalPrice = originalPrice - totalDiscount;
      var percent = Math.round((totalDiscount / originalPrice) * 100);

      discounts[variantId] = {
        percent: percent,
        originalPrice: originalPrice,
        finalPrice: finalPrice,
        totalDiscount: totalDiscount,
        title: discountTitle,
      };
    }

    return discounts;
  }

  /**
   * Envía un batch de líneas al cart y reintenta sin los variants inválidos
   * @param {Array} lines - Array de CartLineInput
   * @param {string} shopDomain - Dominio de la tienda
   * @param {string} token - Storefront API token
   * @param {number} [retryCount=0] - Contador de retries (interno)
   */
  function fetchBatch(lines, shopDomain, token, retryCount) {
    retryCount = retryCount || 0;
    if (lines.length === 0) return Promise.resolve({});

    return callStorefrontAPI(shopDomain, token, CART_CREATE_MUTATION, {
      lines: lines,
    }).then(function (data) {
      if (!data) return {};

      var cartCreate = data.data && data.data.cartCreate;

      if (
        cartCreate &&
        !cartCreate.cart &&
        cartCreate.userErrors &&
        cartCreate.userErrors.length > 0
      ) {
        var failingIndices = {};
        cartCreate.userErrors.forEach(function (err) {
          if (err.field && err.field[1] === "lines") {
            failingIndices[parseInt(err.field[2])] = true;
          }
        });

        var cleanLines = lines.filter(function (_, i) {
          return !failingIndices[i];
        });

        if (cleanLines.length < lines.length && retryCount < MAX_RETRY_COUNT) {
          return fetchBatch(cleanLines, shopDomain, token, retryCount + 1);
        }

        if (retryCount >= MAX_RETRY_COUNT) {
          console.warn(
            "[AutoDiscounts] Max retries reached, some variants may be invalid",
          );
        }

        return {};
      }

      return parseDiscounts(data);
    });
  }

  /**
   * Crea un shadow cart con los variant IDs dados
   * OPTIMIZADO: Batches paralelos para >250 variants
   */
  function fetchDiscounts(variantIds, shopDomain, token) {
    var lines = variantIds.map(function (id) {
      return { merchandiseId: toGID(id), quantity: 1 };
    });

    if (lines.length <= BATCH_SIZE) {
      return fetchBatch(lines, shopDomain, token);
    }

    // OPTIMIZACIÓN: Batches paralelos en lugar de secuenciales
    var batches = [];
    for (var i = 0; i < lines.length; i += BATCH_SIZE) {
      batches.push(lines.slice(i, i + BATCH_SIZE));
    }

    return Promise.all(
      batches.map(function (batch) {
        return fetchBatch(batch, shopDomain, token);
      }),
    ).then(function (results) {
      // Merge all results
      var merged = {};
      for (var i = 0; i < results.length; i++) {
        var parsed = results[i];
        for (var key in parsed) {
          if (parsed.hasOwnProperty(key)) {
            merged[key] = parsed[key];
          }
        }
      }
      return merged;
    });
  }

  // ─── Inicialización PLP ──────────────────────────────────────

  /**
   * Punto de entrada principal para PLP
   */
  function initAutoDiscounts() {
    var config = window.__storefrontApi;

    if (!config || !config.token) return;

    var variantIds = collectVariantIds();
    if (variantIds.length === 0) return;

    var cached = getCachedDiscounts() || {};

    if (Object.keys(cached).length > 0) {
      applyDiscountsToDOM(cached, config.moneyFormat);
    }

    var missingIds = [];
    for (var i = 0; i < variantIds.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(cached, variantIds[i])) {
        missingIds.push(variantIds[i]);
      }
    }

    variantIds.forEach(function (id) {
      queriedVariantIds[id] = true;
    });

    if (missingIds.length === 0) return;

    fetchDiscounts(missingIds, config.shopDomain, config.token).then(
      function (discounts) {
        var merged = Object.assign({}, cached, discounts);
        // Mark fetched variants without discount so we don't re-fetch them
        for (var i = 0; i < missingIds.length; i++) {
          if (!Object.prototype.hasOwnProperty.call(merged, missingIds[i])) {
            merged[missingIds[i]] = null;
          }
        }
        setCachedDiscounts(merged);
        applyDiscountsToDOM(discounts, config.moneyFormat);
      },
    );
  }

  // ─── MutationObserver (OPTIMIZADO: scoped) ───────────────────

  /**
   * Obtiene el contenedor de productos para scope del observer
   */
  function getProductContainer() {
    // Intentar encontrar contenedor específico
    var containers = [
      document.querySelector(".product-grid"),
      document.querySelector("[product-grid]"),
      document.querySelector("#MainContent"),
      document.querySelector("main"),
    ];

    for (var i = 0; i < containers.length; i++) {
      if (containers[i]) return containers[i];
    }

    return document.body; // Fallback
  }

  /**
   * Observa cards nuevos (scroll infinito, filtros AJAX)
   * OPTIMIZADO: Scope a contenedor específico
   */
  function observeNewCards() {
    var config = window.__storefrontApi;
    if (!config || !config.token) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;

          // Nodo ES un ae-product-card
          if (
            node.matches &&
            node.matches("ae-product-card[data-variant-id]")
          ) {
            var id = node.getAttribute("data-variant-id");
            if (id) pendingMutationIds.push(id);
          }
          // Nodo CONTIENE ae-product-cards
          if (node.querySelector) {
            var innerCards = node.querySelectorAll(
              "ae-product-card[data-variant-id]",
            );
            for (var k = 0; k < innerCards.length; k++) {
              var innerId = innerCards[k].getAttribute("data-variant-id");
              if (innerId) pendingMutationIds.push(innerId);
            }
          }
        }
      }

      if (pendingMutationIds.length === 0) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var ids = pendingMutationIds.slice();
        pendingMutationIds = [];
        handleNewCards(config, ids);
      }, MUTATION_DEBOUNCE);
    });

    // OPTIMIZACIÓN: Scope al contenedor de productos
    var container = getProductContainer();
    observer.observe(container, { childList: true, subtree: true });
  }

  /**
   * Maneja cards nuevos que aparecen en la página
   * OPTIMIZADO: Solo procesa cards nuevos, no full DOM scan
   */
  function handleNewCards(config, mutationIds) {
    var cached = getCachedDiscounts() || {};

    // Obtener solo los cards nuevos por sus IDs
    var newCards = [];
    for (var i = 0; i < mutationIds.length; i++) {
      var card = document.querySelector(
        'ae-product-card[data-variant-id="' + mutationIds[i] + '"]',
      );
      if (card) newCards.push(card);
    }

    // Dedup y filtrar IDs ya consultados
    var seen = {};
    var newIds = [];
    for (var j = 0; j < mutationIds.length; j++) {
      var id = mutationIds[j];
      if (!seen[id] && !queriedVariantIds[id]) {
        seen[id] = true;
        newIds.push(id);
      }
    }

    // Aplicar cache solo a los cards nuevos (optimización)
    if (Object.keys(cached).length > 0 && newCards.length > 0) {
      applyDiscountsToDOM(cached, config.moneyFormat, newCards);
    }

    if (newIds.length === 0) return;

    newIds.forEach(function (id) {
      queriedVariantIds[id] = true;
    });

    fetchDiscounts(newIds, config.shopDomain, config.token).then(
      function (newDiscounts) {
        var merged = Object.assign({}, cached, newDiscounts);
        setCachedDiscounts(merged);
        // Aplicar solo a los cards que tuvieron fetch (optimización)
        applyDiscountsToDOM(merged, config.moneyFormat, newCards);
      },
    );
  }

  // ─── PDP (OPTIMIZADO: cache check) ───────────────────────────

  /**
   * Devuelve el badge activo de la PDP
   */
  function getPDPBadge() {
    var activeCarousel = document.querySelector(
      ".variant-carousel:not([hidden])",
    );
    if (activeCarousel)
      return activeCarousel.querySelector("[data-auto-discount-badge]");
    return document.querySelector(
      ".gallery-main-container [data-auto-discount-badge]",
    );
  }

  /**
   * Aplica o resetea el descuento en la PDP
   */
  function applyDiscountToPDP(discount, moneyFormat) {
    var badge = getPDPBadge();
    var productPrice = document.querySelector("product-price");
    var priceContainer = document.querySelector(
      'product-price [ref="priceContainer"]',
    );
    var activeCarousel = document.querySelector(
      ".variant-carousel:not([hidden])",
    );
    var galleryContainer = document.querySelector(".gallery-main-container");
    var pdpPriceBadge = priceContainer
      ? priceContainer.querySelector("[data-auto-discount-badge-pdp]")
      : null;
    var manualDiscountBadge = priceContainer
      ? priceContainer.querySelector(".custom-product-price__badge--off")
      : null;
    var manualSaleBadge = priceContainer
      ? priceContainer.querySelector(".custom-product-price__badge--sale")
      : null;

    // Reset: ocultar badges nativos
    hideBadge(badge);
    hideBadge(pdpPriceBadge);
    if (manualDiscountBadge) manualDiscountBadge.removeAttribute("hidden");
    if (manualSaleBadge) manualSaleBadge.removeAttribute("hidden");

    // Reset: ocultar flags de descuento (tanto en galería como en precio)
    var promoFlagsGallery = galleryContainer
      ? galleryContainer.querySelectorAll("[data-discount-title]")
      : [];
    var promoFlagsPrice = productPrice
      ? productPrice.querySelectorAll("[data-discount-title]")
      : [];
    for (var pf = 0; pf < promoFlagsGallery.length; pf++) {
      promoFlagsGallery[pf].setAttribute("hidden", "");
    }
    for (var pp = 0; pp < promoFlagsPrice.length; pp++) {
      promoFlagsPrice[pp].setAttribute("hidden", "");
    }

    // Reset: limpiar clases en priceContainer
    if (priceContainer) {
      resetPriceContainerClasses(priceContainer);
      var prevDiscountedEl = priceContainer.querySelector(
        "[data-auto-discount-price]",
      );
      if (prevDiscountedEl) {
        prevDiscountedEl.textContent = "";
        prevDiscountedEl.setAttribute("hidden", "");
      }
    }

    if (!discount) return;

    // Check if there's a matching discount flag (buscar tanto en galería como en precio)
    var hasMatchingFlagGallery = galleryContainer
      ? checkMatchingDiscountFlag(galleryContainer, discount.title)
      : false;
    var hasMatchingFlagPrice = productPrice
      ? checkMatchingDiscountFlag(productPrice, discount.title)
      : false;
    var hasMatchingFlag = hasMatchingFlagGallery || hasMatchingFlagPrice;
    if (!hasMatchingFlag) return;

    // Badge handling: PDP tiene lógica específica - badge nativo va al lado del precio
    hideBadge(badge);

    // Precio
    if (!priceContainer || !productPrice) return;

    var compareAtPriceValue = parseInt(
      productPrice.getAttribute("data-compare-at-price") || "0",
      10,
    );
    var productPriceValue = parseInt(
      productPrice.getAttribute("data-product-price") || "0",
      10,
    );
    var hasManualDiscount =
      compareAtPriceValue > 0 && compareAtPriceValue > productPriceValue;

    if (hasManualDiscount) {
      var manualPercent = Math.round(
        ((compareAtPriceValue - productPriceValue) / compareAtPriceValue) * 100,
      );

      if (discount.percent <= manualPercent) {
        hideBadge(pdpPriceBadge);
        return;
      }

      var manualCompareEl = priceContainer.querySelector(
        ".custom-product-price__compare",
      );
      if (manualCompareEl) {
        manualCompareEl.classList.add("price--compare-hidden");
      }

      if (manualDiscountBadge) hideBadge(manualDiscountBadge);
      if (manualSaleBadge) hideBadge(manualSaleBadge);
    } else {
      if (manualDiscountBadge) hideBadge(manualDiscountBadge);
      if (manualSaleBadge) hideBadge(manualSaleBadge);
    }

    hideBadge(pdpPriceBadge);

    showDiscountedPrice(priceContainer, discount.finalPrice, moneyFormat);

    var customPriceEl = productPrice.querySelector(".custom-price");
    if (!customPriceEl) return;

    var configStr = customPriceEl.getAttribute("data-custom-price-config");
    if (!configStr) return;

    try {
      var customConfig = JSON.parse(configStr);
      if (
        customConfig.accumulates === true &&
        typeof discount.finalPrice === "number"
      ) {
        document.dispatchEvent(
          new CustomEvent("custom-price:sync-pdp", {
            detail: {
              host: productPrice,
              effectivePrice: Math.round(discount.finalPrice * 100),
            },
          }),
        );
      }
    } catch (e) {
      // Si data-custom-price-config viene malformado, no rompemos el flujo de descuentos.
      // Solo registramos para detectarlo en dev sin afectar prod (el resto sigue funcionando).
      console.warn("[AutoDiscounts] Invalid data-custom-price-config JSON:", e);
    }
  }

  /**
   * Inicializa los descuentos para la PDP
   * OPTIMIZADO: Verifica cache antes de fetch
   */
  function initPDPDiscounts() {
    var config = window.__storefrontApi;
    if (!config || !config.token) return;

    var productPrice = document.querySelector("product-price[data-variant-id]");
    if (!productPrice) return;

    function fetchAndApply(variantId) {
      // OPTIMIZACIÓN: Check cache primero
      var cached = getCachedDiscounts() || {};

      if (cached[variantId]) {
        // Ya tenemos el dato en cache, aplicarlo inmediatamente
        applyDiscountToPDP(cached[variantId], config.moneyFormat);
        queriedVariantIds[variantId] = true;
        return;
      }

      // Solo fetch si no está en cache
      queriedVariantIds[variantId] = true;
      fetchDiscounts([variantId], config.shopDomain, config.token).then(
        function (discounts) {
          // Merge con cache existente
          var merged = Object.assign({}, cached, discounts);
          setCachedDiscounts(merged);
          applyDiscountToPDP(discounts[variantId] || null, config.moneyFormat);
        },
      );
    }

    var initialVariantId = productPrice.getAttribute("data-variant-id");
    if (initialVariantId) fetchAndApply(initialVariantId);
  }

  // ─── Prefetch al hover (OPTIMIZADO: batch) ───────────────────

  /**
   * Procesa el batch de hovers acumulados
   */
  function processHoverBatch(config) {
    if (hoverPrefetchQueue.length === 0) return;

    // Copiar y limpiar queue
    var idsToFetch = hoverPrefetchQueue.slice();
    hoverPrefetchQueue = [];

    // Fetch en batch
    fetchDiscounts(idsToFetch, config.shopDomain, config.token).then(
      function (newDiscounts) {
        var cached = getCachedDiscounts() || {};
        setCachedDiscounts(Object.assign({}, cached, newDiscounts));
      },
    );
  }

  /**
   * Prefetchea el descuento de cards al hacer hover
   * OPTIMIZADO: Batch acumulado en lugar de llamada por hover
   */
  function initHoverPrefetch() {
    var config = window.__storefrontApi;
    if (!config || !config.token) return;

    document.addEventListener(
      "mouseover",
      function (e) {
        var target = e.target;
        // AE uses ae-product-card custom element
        var card = target.closest("ae-product-card[data-variant-id]");
        if (!card) return;

        var variantId = card.getAttribute("data-variant-id");
        if (
          !variantId ||
          queriedVariantIds[variantId] ||
          hoverPrefetchedVariantIds[variantId]
        )
          return;

        hoverPrefetchedVariantIds[variantId] = true;

        // Agregar al batch queue
        hoverPrefetchQueue.push(variantId);

        // Debounce: esperar a acumular más hovers
        clearTimeout(hoverPrefetchTimer);
        hoverPrefetchTimer = setTimeout(function () {
          processHoverBatch(config);
        }, HOVER_BATCH_DELAY);
      },
      { passive: true },
    );
  }

  // ─── Arranque ────────────────────────────────────────────────

  /**
   * Inicializa el módulo de descuentos automáticos
   * Usa requestIdleCallback para deferir fuera del critical path (mejora performance)
   */
  function init() {
    initAutoDiscounts();
    initPDPDiscounts();
    initHoverPrefetch();
    observeNewCards();
    document.addEventListener("variant:update", handleThemeVariantUpdate, true);
  }

  document.addEventListener("DOMContentLoaded", function () {
    // requestIdleCallback defer ejecución fuera del critical path
    // Fallback a setTimeout si no está disponible (Safari < 14)
    if ("requestIdleCallback" in window) {
      requestIdleCallback(init, { timeout: 1000 });
    } else {
      setTimeout(init, 100);
    }
  });
})();
